import { useState } from 'react';
import { Clock, Check, Send, Trash2, User } from 'lucide-react';

function WaitingItem({ item, isReviewed, onMarkReviewed, onFollowUp, onComplete }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const daysSinceCreated = item.createdAt
    ? Math.floor((new Date() - (item.createdAt.toDate ? item.createdAt.toDate() : new Date(item.createdAt))) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <div className={`
      bg-surface-card rounded-xl border transition-all
      ${isReviewed ? 'border-success/50 opacity-60' : 'border-border'}
    `}>
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Status indicator */}
          <div className={`
            w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
            ${isReviewed ? 'bg-success/20' : 'bg-surface-hover'}
          `}>
            {isReviewed ? (
              <Check size={16} className="text-success" />
            ) : (
              <Clock size={16} className="text-text-secondary" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className={`font-medium ${isReviewed ? 'text-text-tertiary line-through' : 'text-text-primary'}`}>
              {item.title}
            </p>
            <div className="flex items-center gap-2 mt-1">
              {item.waitingFor && (
                <span className="flex items-center gap-1 text-xs text-text-tertiary">
                  <User size={10} />
                  {item.waitingFor}
                </span>
              )}
              {item.delegatedTo && (
                <span className="flex items-center gap-1 text-xs text-accent">
                  <Send size={10} />
                  Delegated to {item.delegatedTo}
                </span>
              )}
              {daysSinceCreated > 7 && (
                <span className="text-xs text-warning bg-warning/10 px-1.5 py-0.5 rounded">
                  {daysSinceCreated}d waiting
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        {!isReviewed && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50">
            <button
              onClick={() => onFollowUp(item.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-accent
                       bg-accent/10 rounded-lg hover:bg-accent/20 border-none cursor-pointer"
            >
              <Send size={12} />
              Follow Up
            </button>
            <button
              onClick={() => onComplete(item.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-success
                       bg-success/10 rounded-lg hover:bg-success/20 border-none cursor-pointer"
            >
              <Check size={12} />
              Received
            </button>
            <button
              onClick={() => onMarkReviewed(item.id)}
              className="ml-auto text-xs text-text-tertiary hover:text-text-secondary
                       bg-transparent border-none cursor-pointer"
            >
              Still Waiting
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function WaitingForReview({
  waitingItems,
  reviewedItems,
  onMarkReviewed,
  onFollowUp,
  onComplete
}) {
  const reviewedCount = reviewedItems.size;

  // Sort: oldest waiting items first, reviewed items last
  const sortedItems = [...waitingItems].sort((a, b) => {
    const aReviewed = reviewedItems.has(a.id);
    const bReviewed = reviewedItems.has(b.id);

    if (aReviewed && !bReviewed) return 1;
    if (!aReviewed && bReviewed) return -1;

    // Sort by creation date (oldest first)
    const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
    const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
    return aDate - bDate;
  });

  return (
    <div className="flex-1 overflow-auto p-4 md:p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-weekly/10 flex items-center justify-center mx-auto mb-4">
            <Clock size={32} className="text-weekly" />
          </div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">Waiting For</h2>
          <p className="text-text-secondary">
            Review items you're waiting on from others. Follow up on stale items.
          </p>
        </div>

        {/* Items list */}
        {waitingItems.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center mx-auto mb-3">
              <Check size={24} className="text-success" />
            </div>
            <p className="text-text-secondary">Nothing pending from others</p>
            <p className="text-sm text-text-tertiary mt-1">You're all caught up!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedItems.map(item => (
              <WaitingItem
                key={item.id}
                item={item}
                isReviewed={reviewedItems.has(item.id)}
                onMarkReviewed={onMarkReviewed}
                onFollowUp={onFollowUp}
                onComplete={onComplete}
              />
            ))}
          </div>
        )}

        {/* Summary */}
        {waitingItems.length > 0 && (
          <div className="mt-6 text-center text-sm text-text-tertiary">
            {reviewedCount} of {waitingItems.length} items reviewed
          </div>
        )}

        {/* Tips */}
        <div className="mt-6 p-4 bg-surface-hover/50 rounded-xl">
          <p className="text-sm text-text-tertiary">
            <strong className="text-text-secondary">Tip:</strong> Items waiting longer than a week
            are highlighted. Consider following up or converting them to your own action.
          </p>
        </div>
      </div>
    </div>
  );
}
